import { FC } from 'react';
import { useIntl } from 'react-intl';
import { isEqual } from 'lodash';
import { useSessionStorage } from 'usehooks-ts';
import { faBook, faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons';

import {
  AddToProjectModalProvider,
  RecordsWithTimestamp,
  SAVED_SEARCHES_KEY,
  SEARCH_HISTORY_KEY,
  VIEWED_RECORDS_KEY,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  useFormattedSearches,
  useRecentActivityInstancesFetch,
  useRecentInstancesRemoval,
  useRecentSearchesFetch,
  useRecentSearchesRemoval,
  useRemoveActivityMutationEffect,
  UserSearchDto,
  useSavedSearchesFetch,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import { HistoryRecordsPageWrapper } from '../HistoryRecordsPageWrapper';
import { DashboardSearchModalProvider } from '../DashboardSearchModal';
import { useBatchInstancesFetch } from '../useBatchInstancesFetch';

export const RecentActivityPage: FC = () => {
  const { isLoggedIn } = useAppContext();
  const { $t } = useIntl();
  const [searchHistory, setSearchHistory] = useSessionStorage<UserSearchDto[]>(
    SEARCH_HISTORY_KEY,
    []
  );
  const formatSearches = useFormattedSearches();
  const instancesQuery = useRecentActivityInstancesFetch();
  const searchesQuery = useRecentSearchesFetch();
  const savedSearchesQuery = useSavedSearchesFetch();
  const [viewedRecords, setViewedRecords] = useSessionStorage<
    RecordsWithTimestamp[]
  >(VIEWED_RECORDS_KEY, []);
  const [savedSearches] = useSessionStorage<UserSearchDto[]>(
    SAVED_SEARCHES_KEY,
    []
  );
  const viewedRecordsDataQuery = useBatchInstancesFetch({
    queryKey: 'viewedRecords',
    list: isLoggedIn ? instancesQuery.data : viewedRecords,
  });
  const removeSearchMutation = useRecentSearchesRemoval();
  const removeInstancesMutation = useRecentInstancesRemoval();

  useRemoveActivityMutationEffect(removeSearchMutation, searchesQuery);
  useRemoveActivityMutationEffect(removeInstancesMutation, instancesQuery);

  const handleSearchEntryRemoval = entryIndex => {
    if (isLoggedIn) {
      removeSearchMutation.mutate([entryIndex]);
    } else {
      setSearchHistory(prevValue =>
        prevValue.filter((searches, index) => index !== entryIndex)
      );
    }
  };

  const handleViewedRecordRemoval = ({
    instanceId,
    timestamp,
    id,
  }: RecordsWithTimestamp) => {
    if (isLoggedIn && id) {
      removeInstancesMutation.mutate([id]);
    } else {
      setViewedRecords(prevValue =>
        prevValue.filter(
          viewedRecord => !isEqual(viewedRecord, { instanceId, timestamp })
        )
      );
    }
  };

  return (
    <DashboardSearchModalProvider>
      <AddToProjectModalProvider>
        <HistoryRecordsPageWrapper
          pageTitle={$t(sharedMessages.recentActivity)}
          parentRoute={ROUTE.recentActivity}
          searchesTabConfig={{
            title: $t(sharedMessages.searchHistory),
            documentTitle: $t({
              id: 'recentActivity.searchHistoryDocumentTitle',
              defaultMessage: 'Search history - Recent activity - EBSCO Locate',
            }),
            route: ROUTE.recentSearchHistory,
            list: isLoggedIn
              ? searchesQuery.data
              : formatSearches(searchHistory),
            isError: searchesQuery.isError,
            isLoading:
              searchesQuery.isFetching ||
              removeSearchMutation.status !== 'idle',
            refetch: searchesQuery.refetch,
            onEntryRemoval: handleSearchEntryRemoval,
            emptyStateMessage: $t({
              id: 'recentActivity.emptyStateSearchMessage',
              defaultMessage: 'You currently have no recent searches.',
            }),
            emptyStateIcon: faMagnifyingGlass,
            savedSearchesList: isLoggedIn
              ? savedSearchesQuery.data
              : formatSearches(savedSearches),
          }}
          recordsTabConfig={{
            title: $t({
              id: 'recentActivity.viewedRecords',
              defaultMessage: 'Viewed records',
            }),
            documentTitle: $t({
              id: 'recentActivity.viewedRecordsDocumentTitle',
              defaultMessage: 'Viewed records - Recent activity - EBSCO Locate',
            }),
            route: ROUTE.recentViewedRecords,
            list: viewedRecordsDataQuery.data,
            isError: instancesQuery.isError || viewedRecordsDataQuery.isError,
            isLoading:
              instancesQuery.isFetching ||
              viewedRecordsDataQuery.isFetching ||
              removeInstancesMutation.status !== 'idle',
            refetch: () =>
              instancesQuery.isError
                ? instancesQuery.refetch()
                : viewedRecordsDataQuery.refetch(),
            onEntryRemoval: handleViewedRecordRemoval,
            emptyStateMessage: $t({
              id: 'recentActivity.emptyStateRecordMessage',
              defaultMessage: 'You currently have no recently viewed records.',
            }),
            emptyStateIcon: faBook,
          }}
        />
      </AddToProjectModalProvider>
    </DashboardSearchModalProvider>
  );
};
